import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { ChangeDetectorRef, Component, OnInit, ViewChild } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { tap, switchMap, catchError, of, Observable, finalize, map } from "rxjs";
import { OrderService } from "src/app/services/order/order.service";
import { PaymentService } from "src/app/payment/services/payment/payment.service";
import { CheckoutService } from "src/app/services/checkout-page/checkout.service";
import { environment } from "src/environments/environment";
import { BaseOrder, OrderAttributes, OrderErrorMessage } from "@models/order.model";
import { InitiatePaymentInput, InitiatePaymentResponse, PaymentGateway, PaymentMethod } from "@models/payment.model";
import { AuthService } from "src/app/services/auth/auth.service";
import { UserService } from "src/app/services/user/user.service";
import {
    BaseAddress,
    NullableAddress,
    NullableAddresses,
    NullableUser,
    UserDetailStatus,
    BaseUser,
    UserDetailsAttributes,
    AddressDetailsAttributes,
    UserDetailsDto,
} from "@models/user.model";
import { DialogService } from "src/app/services/dialog/dialog.service";
import { v4 as uuidv4 } from "uuid";
import { CartDetails, ProductCategories, ProductAttributes } from "src/models/product.model";
import { InitiatePhonePePaymentInput } from "@models/phonepe.model";
import { CartAttributes } from "@models/cart.model";
import { DialogInvokingComponents } from "@models/generic-dialog.model";
import { CouponAttributes, CouponsMessage, DiscountType } from "@models/coupon.model";
import { Location } from "@angular/common";
import { appConfig } from "~/src/environments/app.config";
import { ProfileDetailsComponent } from "~/src/app/user-details/profile-details/profile-details.component";
import { AddressComponent } from "~/src/app/user-details/address/address.component";
import { CartItemService } from "~/src/app/services/cart-item/cart-item.service";
import { RewardsService } from "~/src/app/rewards/rewards.service";
import { CouponService } from "~/src/app/services/coupon/coupon.service";
import { SnackBarConfig } from "~/src/app/constants/snackbar.constants";
import { GenericDialogComponent } from "~/src/app/shared/generic-dialog/generic-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";

@Component({
    selector: "app-address-list",
    templateUrl: "./address-list.component.html",
    styleUrl: "./address-list.component.scss",
    standalone: false,
})
export class AddressListComponent implements OnInit {
    public couponForm: FormGroup;
    public isLoggedIn = false;
    public currentUser?: NullableUser;
    public currentUserAddresses?: NullableAddresses;
    public selectedAddress?: NullableAddress;
    public cartItems: CartAttributes[] = [];
    public cartProducts: ProductAttributes[] = [];
    public loading = false; // State to control spinner
    public orderData!: BaseOrder;
    public paymentGateway = environment.payment.gateway;
    public title!: string;
    public isPayNowEnabled = false;
    public minOrderValue = appConfig.minOrderValue;
    public userId!: number;
    public selectedPaymentMethod: PaymentMethod = PaymentMethod.OnlinePayment;
    public PaymentMethod = PaymentMethod;

    public pageIndex = environment.dynamicPagination.pageIndex;
    public pageSize = environment.dynamicPagination.pageSize;

    public availableCoupons: CouponAttributes[] = [];
    public showCouponSidebar: boolean = false;
    public totalCouponsCount: number = 0;
    public isCouponInvalid: boolean = false;
    public isShowAddressDetailsInput: boolean = false;
    public showCoupons: boolean = false;

    public appliedCoupon: {
        id: number;
        code: string;
        cap: number;
        couponDiscountPercent: number;
        couponDiscountTypes: DiscountType[];
        couponDiscount: number;
        priceAfterCoupon: number;
    } | null = null;

    public totalCoinBalance: number = 0;
    public isUsingCoins: boolean = false;
    public coinsUsed: number | null = null;

    public totalMRP: number = 0;
    public storeDiscount: number = 0;
    public discountedTotal: number = 0;
    public deliveryCharge: number = 0;
    public total: number = 0;
    public amountToBePaid: number = 0;

    public cartDetails: CartDetails[] = [];
    public mobileNumber!: string;
    public categories = ProductCategories;
    public isDirectBuy: boolean = false;
    public productIds!: number[];
    public discountByPercent!: number;
    public firstUserCouponValue!: number;
    public firstUser!: boolean;
    public currentStep: number = 2;
    public profile!: UserDetailsAttributes;
    public address!: BaseAddress;
    public isUserExist: boolean = false;
    public buttonText!: string;
    @ViewChild(ProfileDetailsComponent) profileDetailRef: ProfileDetailsComponent | undefined;
    @ViewChild(AddressComponent) addressDetailRef: AddressComponent | undefined;

    constructor(
        private fb: FormBuilder,
        private paymentService: PaymentService,
        private snackBar: MatSnackBar,
        private orderService: OrderService,
        private authService: AuthService,
        private userService: UserService,
        private checkoutService: CheckoutService,
        private cdr: ChangeDetectorRef,
        private dialogService: DialogService,
        private cartService: CartItemService,
        private rewardsService: RewardsService,
        private router: Router,
        private couponsService: CouponService,
        private location: Location,
        private dialog: MatDialog,
        private _snackBar: MatSnackBar,
        private translate: TranslateService,
    ) {
        this.couponForm = this.fb.group({
            couponCode: ["", Validators.required],
        });
    }

    ngOnInit(): void {
        // Check login status
        this.isLoggedIn = this.authService.isUserLoggedIn;
        let guestUserLogin = false; // variable used for when guest user wants to login from checkout page having only cart items

        // Load user data based on login status
        if (this.isLoggedIn) {
            this.userService.isUserDetailsExist().subscribe((response) => {
                this.isUserExist = response;
            });
            this.getRegisteredUserDetails();
        } else {
            this.isUserExist = false;
            this.getGuestUserDetails();
        }

        // Handle cart product data
        const product = history.state.productData;
        this.title = product ? "Item(s)" : "Items in Cart";
        this.cartService.cartSyncComplete$.subscribe((res) => {
            if (res && !product) {
                this.loadCartItems();
                guestUserLogin = true;
            }
        });

        if (!guestUserLogin) {
            product ? this.loadPassedItem() : this.loadCartItems();
        }

        // If user is logged in, fetch rewards and user-specific data
        if (this.isLoggedIn) {
            this.getRewardsData();

            this.userService.getUserDetails().subscribe((response) => {
                if (!response) return;

                this.userId = response.id;
                this.mobileNumber = response.mobileNumber;
                this.fetchAvailableCoupons();
                this.orderService
                    .isFirstOrder()
                    .pipe(
                        tap((result) => {
                            if (!result?.exists) {
                                this.couponsService.getCouponByType(DiscountType.FirstUser).subscribe((coupon) => {
                                    if (coupon.length && coupon[0].isActive) {
                                        const couponDiscount = Math.floor(
                                            Math.min(
                                                coupon[0].maxAmount,
                                                (this.discountedTotal * coupon[0].discountPercentage) / 100,
                                            ),
                                        );
                                        this.appliedCoupon = {
                                            id: coupon[0].id!,
                                            code: coupon[0].couponCode,
                                            cap: coupon[0].maxAmount!,
                                            couponDiscountPercent: coupon[0].discountPercentage,
                                            couponDiscountTypes: coupon[0].discountType,
                                            couponDiscount,
                                            priceAfterCoupon: this.discountedTotal - couponDiscount,
                                        };
                                        this.updateFinalPrice();
                                    }
                                });
                            }
                        }),
                    )
                    .subscribe();
            });
        }
    }

    public handleProceed(): void {
        if (!this.isMoreOrEqualToMinOrderValue()) {
            return;
        }
        if (this.amountToBePaid === 0) {
            this.handlePlaceOrder(); // Reward order
        } else {
            this.handlePayment(); // COD or Online
        }
    }

    public handlePayment(): void {
        if (!this.isMoreOrEqualToMinOrderValue()) {
            return;
        }
        if (this.selectedPaymentMethod === PaymentMethod.OnlinePayment) {
            this.paymentGateway === PaymentGateway.Paytm ? this.makePaytmPayment() : this.makePhonepePayment();
        } else if (this.selectedPaymentMethod === PaymentMethod.CashOnDelivery) {
            this.proceedWithCOD();
        }
    }

    //user wants to do payment with Rewards only
    public handlePlaceOrder() {
        if (!this.isMoreOrEqualToMinOrderValue()) {
            return;
        }
        this.selectedPaymentMethod = PaymentMethod.Reward;
        this.loading = true;

        this.prepareOrderData()
            .then(() => {
                this.orderService
                    .createOrder(this.orderData, this.selectedPaymentMethod)
                    .pipe(
                        finalize(() => {
                            this.loading = false;
                        }),
                    )
                    .subscribe({
                        next: (response) => {
                            if (this.appliedCoupon && this.mobileNumber) {
                                this.couponsService
                                    .markCouponAsUsed(this.appliedCoupon.code, this.mobileNumber)
                                    .subscribe({
                                        next: () => {
                                            this.snackBar.open(
                                                CouponsMessage.couponApplied,
                                                SnackBarConfig.Actions.CLOSE,
                                                {
                                                    duration: SnackBarConfig.Duration.MEDIUM,
                                                },
                                            );
                                        },
                                        error: (error) => {
                                            this.snackBar.open(error.message, SnackBarConfig.Actions.CLOSE, {
                                                duration: SnackBarConfig.Duration.MEDIUM,
                                            });
                                        },
                                    });
                            }
                            this.router.navigate(["order/success"], {
                                queryParams: { orderId: response.id },
                            });
                        },
                        error: (error) => {
                            this.snackBar.open("Failed to create order", SnackBarConfig.Actions.CLOSE, {
                                duration: SnackBarConfig.Duration.MEDIUM,
                            });
                            console.error("Error creating order", error);
                        },
                    });
            })
            .catch((error) => {
                // Handle errors during order data preparation
                this.loading = false;
                this.snackBar.open("Failed to prepare order data", SnackBarConfig.Actions.CLOSE, {
                    duration: SnackBarConfig.Duration.MEDIUM,
                });
                console.error("Error preparing order data", error);
            });
    }

    private loadPassedItem(): void {
        const productData = history.state.productData;
        if (productData && productData.length > 0) {
            this.cartItems = productData;
            this.isDirectBuy = true;
            this.cartDetails = this.cartItems.map((item) => ({
                product: item.product!,
                singleBlockInfo: item.singleBlockInfo!,
            }));
            this.productIds = this.cartItems.map((item) => item.product?.id!);
            this.cartProducts = this.cartItems.map((item) => item.product!);

            this.cdr.detectChanges();
        }

        // Ensure cartProducts are initialized before calculating summary
        if (this.cartProducts && this.cartProducts.length > 0) {
            this.calculateOrderSummary();
        }
    }

    private loadCartItems(): void {
        this.loading = true;
        this.cartService.getCartItems().subscribe((items) => {
            this.cartItems = items.filter((item) => item.product?.isActive);
            this.isDirectBuy = false;
            this.cartDetails = this.cartItems.map((item) => ({
                product: item.product!,
                singleBlockInfo: item.singleBlockInfo!,
            }));
            this.productIds = this.cartItems.map((item) => item.product?.id!);
            this.cartProducts = this.cartItems.map((item) => item.product!);
            this.loading = false;
            this.cdr.detectChanges();
            this.calculateOrderSummary();
        });
    }

    private calculateOrderSummary(): void {
        this.cartService.calculateTotalMRP(this.cartItems);
        this.totalMRP = this.cartService.totalMRP;
        this.storeDiscount = this.cartService.totalDiscount;
        this.discountedTotal = this.cartService.subTotal;
        this.isPayNowEnabled = this.isMoreOrEqualToMinOrderValue();
        this.updateFinalPrice();
    }

    private getGuestUserDetails(): void {
        const address = this.checkoutService.getAddressFromLocalStorage();
        if (address) {
            this.selectedAddress = address;
            this.currentUserAddresses = [this.selectedAddress];
        }

        this.currentUser = this.checkoutService.getCurrentUserFromLocalStorage();
    }

    // Registered Users: Get Details from Database
    private getRegisteredUserDetails(): void {
        this.userService.getUserDetails().subscribe((response) => {
            if (response) {
                this.currentUser = response.user;
                this.buttonText = this.currentUser ? "Change Address" : "Add Address";
                this.userId = response.id;
                this.currentUserAddresses = response.addresses;
                this.selectedAddress = response.addresses?.find((one) => one.isPreferred);
            }
        });
    }

    private prepareOrderData(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const products = this.cartItems.map((item) => ({
                id: item.product!.id,
                quantity: item.quantity,
                singleBlockInfo: item.singleBlockInfo!,
            }));
            this.orderData = {
                products,
                ...(this.appliedCoupon && {
                    couponId: this.appliedCoupon.id,
                }),
                isCoinUsed: this.isUsingCoins,
                deliveryCharge: this.deliveryCharge,
                customerName: this.currentUser!.name,
                customerEmail: this.currentUser!.email,
                customerMobile: this.currentUser!.userMobileNumber!,
                addressID: this.selectedAddress?.id,
                recipientName: this.selectedAddress?.name || "",
                recipientMobileNumber: this.selectedAddress?.mobileNumber || "",
                recipientAddressType: this.selectedAddress?.addressType || "",
                recipientAddress: this.selectedAddress?.address || "",
                recipientCity: this.selectedAddress?.city || "",
                recipientState: this.selectedAddress?.state || "",
                recipientPincode: this.selectedAddress?.pincode || 0,
                recipientHouseNumber: this.selectedAddress?.houseNumber || "",
                isFromCart: !this.isDirectBuy,
            };

            resolve();
        });
    }

    // Create Order Record and inititate payment
    public makePaytmPayment(): void {
        this.loading = true;
        this.prepareOrderData().then(() => {
            this.orderService
                .createOrder(this.orderData, this.selectedPaymentMethod)
                .pipe(
                    tap(this.validateOrderCreation),
                    switchMap((newOrder) => this.preparePaymentData(newOrder)),
                    switchMap((paymentData) => this.paymentService.initiatePayment(paymentData)),
                    tap(this.handleInitiatePaymentResponse),
                    catchError(this.handleError),
                )
                .subscribe({
                    next: () => {
                        this.loading = false;
                    },
                    error: () => {
                        this.loading = false; // Stop spinner in case of subscription error
                    },
                });
        });
    }
    /** PhonePe Integration  **/
    public makePhonepePayment(): void {
        this.loading = true;
        of(null)
            .pipe(
                // Conditionally fetch user details if logged in
                switchMap(() => {
                    if (this.isLoggedIn) {
                        return this.userService.getUserDetails().pipe(
                            tap((response) => {
                                if (response) {
                                    this.userId = response.id;
                                }
                            }),
                        );
                    } else {
                        return of(null); // If not logged in, skip this step
                    }
                }),
            )
            .subscribe({
                next: () => {
                    this.prepareOrderData().then(() => {
                        this.preparePhonePePaymentData().subscribe((paymentData: InitiatePhonePePaymentInput) => {
                            this.paymentService.initiatePhonePePayment(paymentData).subscribe({
                                next: (response) => {
                                    this.loading = false;
                                    const payPageUrl = response.url;
                                    // Handle coupon usage after payment success
                                    if (this.appliedCoupon) {
                                        const userId = this.mobileNumber;
                                        this.couponsService
                                            .markCouponAsUsed(this.appliedCoupon.code, userId)
                                            .subscribe({
                                                next: () => {
                                                    this.snackBar.open(
                                                        CouponsMessage.couponApplied,
                                                        SnackBarConfig.Actions.CLOSE,
                                                        { duration: SnackBarConfig.Duration.MEDIUM },
                                                    );
                                                    window.location.href = payPageUrl!;
                                                },
                                                error: (error) => {
                                                    this.snackBar.open(error.message, SnackBarConfig.Actions.CLOSE, {
                                                        duration: SnackBarConfig.Duration.MEDIUM,
                                                    });
                                                },
                                            });
                                    } else {
                                        window.location.href = payPageUrl!;
                                    }
                                },
                                error: (error) => {
                                    alert(error.error);
                                    this.loading = false;
                                },
                            });
                        });
                    });
                },
            });
    }

    /** Prepare phonePe Request Data  **/
    private preparePhonePePaymentData(): Observable<InitiatePhonePePaymentInput> {
        const paymentData: InitiatePhonePePaymentInput = {
            amount: this.amountToBePaid,
            orderData: this.orderData,
        };
        return of(paymentData);
    }

    /** Handle for  COD **/
    public proceedWithCOD(): void {
        this.loading = true;
        this.prepareOrderData().then(() => {
            this.orderService
                .createOrder(this.orderData, PaymentMethod.CashOnDelivery)
                .pipe(
                    finalize(() => {
                        this.loading = false;
                    }),
                )
                .subscribe({
                    next: (response) => {
                        if (this.appliedCoupon && this.mobileNumber) {
                            this.couponsService.markCouponAsUsed(this.appliedCoupon.code, this.mobileNumber).subscribe({
                                next: () => {
                                    this.snackBar.open(CouponsMessage.couponApplied, SnackBarConfig.Actions.CLOSE, {
                                        duration: SnackBarConfig.Duration.MEDIUM,
                                    });
                                },
                                error: (error) => {
                                    this.snackBar.open(error.message, SnackBarConfig.Actions.CLOSE, {
                                        duration: SnackBarConfig.Duration.MEDIUM,
                                    });
                                },
                            });
                        }

                        this.router.navigate(["order/success"], {
                            queryParams: { orderId: response.id },
                        });
                    },
                    error: (error) => {
                        alert(error.error);
                    },
                });
        });
    }

    /** Paytm **/
    private validateOrderCreation = (response: OrderAttributes): void => {
        if (!response.id) {
            this.loading = false;
            throw new Error(OrderErrorMessage.OrderNotCreated); // Custom error message
        }
    };

    /** Paytm **/
    private preparePaymentData(newOrder: OrderAttributes): Observable<InitiatePaymentInput> {
        const generatedGuid: string = uuidv4();
        const paymentData: InitiatePaymentInput = {
            orderId: `${environment.name}-${newOrder.id.toString()}-${generatedGuid}`,
            userInfo: {
                custId: newOrder.customerMobile,
            },
            callbackUrl: environment.apiUrlBasePath + environment.payment.paytmConfig.merchant.callbackUrl,
            txnAmount: {
                value: newOrder.originalPaymentAmount!.toString(),
                currency: "INR",
            },
        };

        return of(paymentData);
    }

    /** Paytm **/
    private handleInitiatePaymentResponse = (response: InitiatePaymentResponse): void => {
        if (response.body.txnToken) {
            this.navigateToCheckout(response);
        } else {
            this.loading = false; // Stop spinner
            throw new Error(response.body.resultInfo.resultMsg); // Display error message
        }
    };

    private navigateToCheckout(response: InitiatePaymentResponse): void {
        this.router.navigate(["payment"], {
            queryParams: {
                orderId: response.body.orderId,
                txnToken: response.body.txnToken,
                totalAmount: response.body.amount,
            },
        });
    }

    private handleError = (error: Error): Observable<never> => {
        this.loading = false;
        this.openSnackBar(error.message, "Okay");
        return of();
    };

    public numericOnly(event: KeyboardEvent): boolean {
        let patt = /^([0-9])$/;
        let result = patt.test(event.key);
        return result;
    }

    public onLogin(): void {
        this.router.navigate(["login"]);
    }

    private selectAddressById(id: number): void {
        const address = this.currentUserAddresses!.find((address) => address!.id === id);
        if (address) {
            this.selectedAddress = address;
            this.userService.updatePreferredAddress(id).subscribe({
                next: (response) => {
                    if (response) {
                        this.getRegisteredUserDetails();
                        this.openSnackBar(response.message, "Close");
                    }
                },
                error: (error) => {
                    throw new Error(error.message);
                },
            });
        }
    }

    private addUserDetailsAndSelectNewAddress(profile: BaseUser, newAddress: BaseAddress): void {
        this.userService
            .addUserDetails({
                userDetails: profile,
                addressDetails: newAddress,
            })
            .subscribe((response) => {
                if (response) {
                    this.openSnackBar(UserDetailStatus.Add, "Close");
                    this.isShowAddressDetailsInput = false;
                }
                this.getRegisteredUserDetails();
            });
    }

    private addAddressAndSelectNewAddress(newAddress: BaseAddress): void {
        this.userService.addAddress(newAddress).subscribe((newAddress) => {
            if (newAddress) {
                this.openSnackBar(UserDetailStatus.Add, "Close");
                this.isShowAddressDetailsInput = false;
            }
            this.getRegisteredUserDetails();
        });
    }

    public isOverflow(el: HTMLElement): boolean {
        return el.clientWidth < el.scrollWidth || el.clientHeight < el.scrollHeight;
    }

    //For future use
    public onTermsAndCondition(): void {
        this.router.navigate(["/terms-and-conditions"]);
    }

    //For future use
    public onPrivacyPolicy(): void {
        this.router.navigate(["/privacy-policy"]);
    }

    public getRewardsData(): void {
        this.userService
            .getUserDetails()
            .pipe(
                switchMap((response) => {
                    if (response) {
                        const userId = response.id;
                        return this.rewardsService.getRewardsWithTotalCountForUser(
                            userId,
                            this.pageIndex,
                            this.pageSize,
                        );
                    }
                    return of(null);
                }),
            )
            .subscribe((data) => {
                if (data) {
                    this.totalCoinBalance = data.balance;
                }
            });
    }

    public calculateTotalBalance(totalCoinsEarned: number, totalCoinsSpent: number): number {
        return totalCoinsEarned - totalCoinsSpent;
    }

    public updateFinalPrice(): void {
        const deliveryCharge = this.deliveryCharge || 0;

        let baseTotal = this.appliedCoupon ? this.appliedCoupon.priceAfterCoupon : this.discountedTotal;

        // Add delivery charges and ensure total is non-negative
        this.total = Math.max(0, baseTotal + deliveryCharge);

        // Calculate coin usage
        this.coinsUsed = this.isUsingCoins ? Math.min(this.totalCoinBalance, this.total) : null;

        // Final amount to be paid after applying coins
        this.amountToBePaid = Math.max(0, this.coinsUsed ? this.total - this.coinsUsed : this.total);
    }

    // public onUseCoinsChange(event: MatCheckboxChange): void {
    //   this.isUsingCoins = event.checked;
    //   this.updateFinalPrice();
    // }

    public removeCoupon(): void {
        this.appliedCoupon = null;
        this.updateFinalPrice();
        this.couponForm.get("couponCode")?.setValue("");
        this.couponForm.markAsPristine();
    }

    public fetchAvailableCoupons(): void {
        this.couponsService.getUserCoupons().subscribe({
            next: (response) => {
                this.availableCoupons = response.filter((data) => {
                    return (
                        !data.isUsed?.userIds.includes(this.mobileNumber) &&
                        Object.values(data.discountType).find((type) => type === "Users")
                    );
                });
            },
        });
    }

    public openCouponSidebar(): void {
        this.showCouponSidebar = !this.showCouponSidebar;
    }

    public closeCouponSidebar(): void {
        this.showCouponSidebar = false;
    }

    public copyCouponCode(couponCode: string): void {
        navigator.clipboard.writeText(couponCode).then(() => {
            this.snackBar.open(CouponsMessage.couponsSnackBar, SnackBarConfig.Actions.CLOSE, {
                duration: SnackBarConfig.Duration.MEDIUM,
            });
        });
    }

    public getFacesByType(data: any, type: string): string[] {
        return this.cartService.getFacesByType(data, type);
    }

    public getFaceColors(colorObject: object | undefined): { key: string; value: string }[] {
        if (!colorObject) {
            return [];
        }
        return Object.entries(colorObject).map(([key, value]) => ({
            key,
            value: value as string, // Ensure the value is treated as a string
        }));
    }
    public isMoreOrEqualToMinOrderValue(): boolean {
        return this.discountedTotal >= this.minOrderValue;
    }

    public goToProductDetail(productId: number) {
        this.router.navigate([`products/${productId}`]);
    }

    public getdiscountPercentage(product: ProductAttributes): number {
        return Math.round(((product.price - product?.discountedPrice!) / product.price) * 100);
    }

    public isFirstUserCoupon(): boolean {
        if (!this.appliedCoupon) return false;
        return this.appliedCoupon.couponDiscountTypes.includes(DiscountType.FirstUser);
    }

    public goBack(): void {
        this.location.back();
    }

    public onAddressChange(id: number): void {
        this.selectAddressById(id);
    }

    public AddNewAddress(): void {
        const dialogData = {
            componentName: DialogInvokingComponents.AddAddress,
            title: "Add Delivery Address",
            withUserProfileDetails: this.currentUser ? false : true,
        };
        this.dialogService
            .openDialog(dialogData, "90%", { disableClose: true })
            .afterClosed()
            .subscribe((response) => {
                if (response) {
                    const payload = response.details ? { ...response.details } : response;
                    this.currentUser ? this.handleAddress(payload) : this.handleUser(payload);
                }
            });
    }

    private handleAddress(payload: BaseAddress) {
        if (!this.authService.isUserLoggedIn) {
            this.checkoutService.setAddressInLocalStorage(payload);
            this.loading = true;
            setTimeout(() => {
                this.getGuestUserDetails();
                this.loading = false;
            }, 1000);
        } else {
            this.userService.addAddress(payload).subscribe({
                next: () => {
                    this.openSnackBar(UserDetailStatus.Add, SnackBarConfig.Actions.CLOSE);
                    this.getRegisteredUserDetails();
                },
            });
        }
    }

    private handleUser(payload: UserDetailsDto) {
        if (!this.authService.isUserLoggedIn) {
            //guest user
            if (!this.isUserExist) {
                // No profile yet
                this.addUserDetailsInLocalStorage(payload);
            }
        } else {
            this.userService.addUserDetails(payload).subscribe(() => {
                this.openSnackBar(UserDetailStatus.Add, SnackBarConfig.Actions.CLOSE);
                this.getRegisteredUserDetails();
            });
        }
    }

    private addUserDetailsInLocalStorage(payload: UserDetailsDto): void {
        this.checkoutService.setUserInLocalStorage(payload.userDetails);
        this.checkoutService.setAddressInLocalStorage(payload.addressDetails);
        this.loading = true;
        setTimeout(() => {
            this.loading = false;
            this.getGuestUserDetails();
        }, 1000);
    }

    public receiveProfileData(profile: UserDetailsAttributes): void {
        this.profile = profile;
    }

    public receiveAddressData(address: BaseAddress): void {
        this.address = address;
    }

    public isFormDataValid(): boolean {
        if (!this.currentUser) {
            return !!(this.profileDetailRef?.profileForm.valid && this.addressDetailRef?.addressForm.valid);
        } else {
            return !!this.addressDetailRef?.addressForm.valid;
        }
    }

    public submitForm(): void {
        if (!this.currentUser) {
            this.addUserDetailsAndSelectNewAddress(this.profile, this.address);
        } else {
            this.addAddressAndSelectNewAddress(this.address);
        }
    }

    public goToCart(): void {
        this.router.navigate([`/cart`]);
    }

    public editAddress(id: number): void {
        this.router.navigate([`/editAddress/${id}`]);
    }

    public deleteAddress(address: AddressDetailsAttributes) {
        this.openDialog(address);
    }

    private openDialog(address: AddressDetailsAttributes): void {
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: {
                componentName: DialogInvokingComponents.DeleteAddress,
                title: this.getTranslation("genericDialogBox.deleteAddress.title"),
                content: this.getTranslation("genericDialogBox.deleteAddress.content"),
                firstBtn: this.getTranslation("genericDialogBox.button.deleteBtn"),
                secondBtn: this.getTranslation("genericDialogBox.button.cancelBtn"),
            },
            autoFocus: false,
            restoreFocus: false,
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.onDeleteAddress(address.id);
            }
        });
    }

    private getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    private onDeleteAddress(id: number): void {
        this.userService.deleteAddress(id).subscribe(
            (response) => {
                this.getRegisteredUserDetails();
                this.openSnackBar(response, SnackBarConfig.Actions.OKAY);
            },
            (error) => {
                throw new Error(error.message);
            },
        );
    }

    private openSnackBar(message: string, action: string): void {
        this._snackBar.open(message, action, {
            ...SnackBarConfig.Defaults,
        });
    }

    public openDialogForPayment(): void {
        const dialogData = {
            componentName: DialogInvokingComponents.PaymentDialog,
            totalCoinBalance: this.totalCoinBalance,
            totalAmount: this.total,
            isCoinUsed: this.isUsingCoins,
        };

        const dialogRef = this.dialogService.openDialog(dialogData, "90%", { disableClose: true }, "512px");

        // Listen to live updates
        dialogRef.componentInstance.coinUsedChange.subscribe((isUsed: boolean) => {
            this.isUsingCoins = isUsed;
            this.updateFinalPrice();
        });

        dialogRef.afterClosed().subscribe((response) => {
            if (response.paymentOption === PaymentMethod.OnlinePayment) {
                this.makePhonepePayment();
            } else if (response.paymentOption === PaymentMethod.CashOnDelivery) {
                this.proceedWithCOD();
            }
        });
    }

    public openDialogForCoupons(): void {
        const dialogData = {
            componentName: DialogInvokingComponents.CouponDialog,
            availableCoupons: this.availableCoupons,
        };
        this.dialogService.openDialog(dialogData, "90%", { disableClose: true }, "700px");
    }

    public toggleCouponshow(): void {
        this.showCoupons = !this.showCoupons;
    }

    public applyCoupon(couponCode: string): void {
        const userId = this.mobileNumber;
        if (couponCode && userId) {
            this.couponsService.checkAndUseCoupon(couponCode, userId, this.productIds).subscribe({
                next: (response) => {
                    if (response.used) {
                        this.appliedCoupon = null;
                        this.snackBar.open(CouponsMessage.couponUsed, SnackBarConfig.Actions.CLOSE, {
                            duration: SnackBarConfig.Duration.MEDIUM,
                        });
                    } else {
                        const couponDiscount = Math.floor(
                            Math.min(response.maxAmount, (this.discountedTotal * response.discountPercentage) / 100),
                        );
                        this.appliedCoupon = {
                            id: response.couponId,
                            code: response.couponCode,
                            cap: response.maxAmount,
                            couponDiscountPercent: response.discountPercentage,
                            couponDiscountTypes: response.discountType,
                            couponDiscount,
                            priceAfterCoupon: this.discountedTotal - couponDiscount,
                        };
                        this.isCouponInvalid = false;
                        this.snackBar.open(CouponsMessage.couponApplied, SnackBarConfig.Actions.CLOSE, {
                            duration: SnackBarConfig.Duration.MEDIUM,
                        });
                        this.updateFinalPrice();
                    }
                },
                error: () => {
                    this.snackBar.open(CouponsMessage.couponNotFound, SnackBarConfig.Actions.CLOSE, {
                        duration: SnackBarConfig.Duration.MEDIUM,
                    });
                    this.isCouponInvalid = true;
                },
            });
        }
    }
}
